<script setup lang="ts">
import LxcButton from "@lxc/app-device-common/src/components/LxcButton.vue";
import { storeToRefs } from "pinia";
import { useAcl } from "vue-simple-acl";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { useUpdateDtwinLifeCycleState } from "~/modules/dtwin/composables/useUpdateDtwinLifeCycleState";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import type { DtwinActionButtonConfiguration } from "~/modules/dtwin/models/DtwinActionButtonConfiguration.interface";
import { DtwinLifeCycleState } from "~/modules/dtwin/models/DtwinLifeCycleState.enum";
import { DtwinListDisplayContext } from "~/modules/dtwin/models/DtwinListDisplayContext.enum";
import type { DtwinModel } from "~/modules/dtwin/models/DtwinModel.interface";
import { canEnableDisableDtwin } from "~/modules/dtwin/utils/canEnableDisableDtwin";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import ILxcLightRotateCw from "~icons/lxc-light/rotate-cw";
import ILxcLightSlash from "~icons/lxc-light/slash";
import ILxcLightTrash2 from "~icons/lxc-light/trash-2";

const props = defineProps<{
  context?: DtwinListDisplayContext;
  isRemovingFromFleet?: boolean;
  isAllowedToManageFleet?: boolean;
  dtwin: Dtwin;
  dtwinModel?: DtwinModel;
}>();

const emits = defineEmits(["removeFromFleet", "lifeCycleStateUpdated"]);

const { t } = useI18n();
const { can } = useAcl();
const { currentOrganization } = storeToRefs(useOrganizationStore());
const { error, isLoading, updateDtwinLifeCycleState } =
  useUpdateDtwinLifeCycleState({
    organizationCode: currentOrganization.value?.code,
  });

const canUpdateLifeCycleState = computed(() => {
  // cannot update life cycle state if the user does not have the required permissions
  if (!can(ACLRoles.CAN_WRITE_DEVICE)) {
    return false;
  }

  return canEnableDisableDtwin(props.dtwin, props.dtwinModel);
});

const showConfirm = ref(false);

const actionConfiguration: ComputedRef<DtwinActionButtonConfiguration> =
  computed(() => {
    if (props.dtwin.lifeCycleState === DtwinLifeCycleState.DISABLED) {
      return {
        icon: ILxcLightRotateCw,
        title: t("dtwins.action.enable"),
        content: t("dtwins.action.button.enable"),
      };
    } else {
      return {
        icon: ILxcLightSlash,
        title: t("dtwins.action.disable"),
        content: t("dtwins.action.button.disable"),
      };
    }
  });

const onTriggerAction = () => {
  if (
    props.dtwin.lifeCycleState === DtwinLifeCycleState.DISABLED &&
    props.dtwin.previousLifeCycleState
  ) {
    onUpdateDtwinLifeCycleState(props.dtwin.previousLifeCycleState);
  } else {
    showConfirm.value = true;
  }
};

async function onUpdateDtwinLifeCycleState(
  newLifeCycleState: DtwinLifeCycleState,
) {
  await updateDtwinLifeCycleState(newLifeCycleState, props.dtwin.uid);

  if (error.value) {
    error.value.notify(NotificationKey.error);
  } else {
    showNotificationSuccess(t(NotificationKey.success));
    emits("lifeCycleStateUpdated");
  }
  showConfirm.value = false;
}
</script>

<template>
  <!-- Context: device list on fleet => button remove the device from a fleet -->
  <lxc-button
    v-if="context === DtwinListDisplayContext.FLEET"
    html-type="button"
    type="borderless"
    :disabled="isRemovingFromFleet || !isAllowedToManageFleet"
    :icon="!isRemovingFromFleet ? ILxcLightTrash2 : undefined"
    :title="
      isAllowedToManageFleet
        ? t('dtwins.fleet.removeFromFleet')
        : t('fleet.actionForbidden')
    "
    class="invisible"
    @click.stop.prevent="$emit('removeFromFleet')"
  >
    <lxc-loader v-if="isRemovingFromFleet" />
  </lxc-button>

  <!-- Context: device list => button enable/disable -->
  <lxc-button
    v-else-if="
      context === DtwinListDisplayContext.DTWINS && canUpdateLifeCycleState
    "
    html-type="button"
    type="borderless"
    class="invisible !px-0 !py-0"
    :title="actionConfiguration.title"
    :disabled="isLoading"
    :icon="!isLoading ? actionConfiguration.icon : undefined"
    @click.stop.prevent="onTriggerAction"
  >
    <lxc-loader v-if="isLoading" />
  </lxc-button>

  <!-- Confirm modal on disable -->
  <confirm-modal
    :is-dialog-visible="showConfirm"
    :title="t('dtwins.action.confirm.title')"
    :description="t('dtwins.action.confirm.description')"
    :cancel-label="t('button.cancel')"
    :ok-label="t('dtwins.action.confirm.disable')"
    :icon="ILxcLightTrash2"
    icon-color-theme="error"
    color-theme="danger"
    :is-loading="isLoading"
    @confirm="onUpdateDtwinLifeCycleState(DtwinLifeCycleState.DISABLED)"
    @cancel="showConfirm = false"
  />
</template>

<style lang="scss" scoped>
button {
  :deep(svg) {
    height: 20px;
    width: 20px;
  }
}
</style>
